import api from "./api";

const FINS_PO_BASE = "/fulfilment/order/api/v1/po";
const ATTACHMENTS_PATH = "/fulfilment/accounts-payable/api/v2/attachments";

async function fetchPOWithDraft(poId) {
  const live = await api.get(`${FINS_PO_BASE}/${poId}?include=attachments`);
  if (live?.has_draft) {
    const draft = await api.get(`${FINS_PO_BASE}/${poId}?draft=true`);
    return { ...live, draft };
  }
  return live;
}

export async function getPOById(poId) {
  return fetchPOWithDraft(poId);
}

export async function getPOByCode(poCode) {
  const result = await api.get(
    `${FINS_PO_BASE}?filters=po_no.eq:${encodeURIComponent(poCode)}`
  );
  const summary = result?.data?.[0];
  if (!summary) {
    throw new Error(`PO ${poCode} not found in FINS`);
  }
  return fetchPOWithDraft(summary.id);
}

export async function createAttachmentRecord(payload) {
  return api.post(ATTACHMENTS_PATH, JSON.stringify(payload));
}

export async function confirmAttachment(id) {
  return api.post(`${ATTACHMENTS_PATH}/${id}/confirm`, JSON.stringify({}));
}

export async function uploadFileToPresignedUrl(file, url) {
  const res = await fetch(url, { method: "PUT", body: file });
  if (!res.ok) {
    throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`);
  }
}

function sanitizeFileName(fileName) {
  const lastDotIndex = fileName.lastIndexOf(".");
  const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : "";
  const nameWithoutExt =
    lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
  const sanitized = nameWithoutExt
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${sanitized}${extension}`;
}

// Confirm de-duplicates identical bytes: it soft-deletes the record it was asked
// about and returns the existing one, so only the confirmed id stays valid. The
// PUT URL belongs to the record confirm may have deleted, so it is dropped when the
// id changed; the next upload then creates a fresh record.
function confirmedAttachment(uploaded, confirmed) {
  const id = confirmed?.id ?? uploaded.id;
  return { ...uploaded, id, url: id === uploaded.id ? uploaded.url : null };
}

export async function uploadAttachment(file) {
  const attachment = await createAttachmentRecord({
    name: sanitizeFileName(file.name),
  });
  await uploadFileToPresignedUrl(file, attachment.url);
  return confirmedAttachment(attachment, await confirmAttachment(attachment.id));
}

// Replaces the bytes of an existing attachment through its presigned PUT URL.
export async function reuploadAttachment(file, attachment) {
  await uploadFileToPresignedUrl(file, attachment.url);
  return confirmedAttachment(attachment, await confirmAttachment(attachment.id));
}

export const CREDIT_NOTE_FINANCE_APPROVAL_STEP_NAMES = [
  "credit_note_finance_approval",
];

export async function getCreditNoteWorkflowRequestForPO(poNoOrList) {
  const list = (Array.isArray(poNoOrList) ? poNoOrList : [poNoOrList]).filter(
    Boolean,
  );
  if (list.length === 0) throw new Error("at least one po_no is required");
  const tagsInValue = list.map((po) => `po_no:${po}`).join(",");
  const filters = [
    "request_types:po_approval_workflow,po_edit_workflow,po_cancel_workflow",
    "status:OPEN",
    `current_steps.name:${CREDIT_NOTE_FINANCE_APPROVAL_STEP_NAMES.join(",")}`,
    "steps.status:PENDING",
    `tagsIn:${tagsInValue}`,
  ].join(";");
  const count = Math.max(10, list.length * 3);
  const params = new URLSearchParams({
    start: "0",
    count: String(count),
    include_all_steps: "true",
    filters,
  });
  return api.get(`/core/workflow/api/v2/requests?${params.toString()}`);
}
